import type { SerializedError } from '@reduxjs/toolkit';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { ApiServerError } from '@cp/utils/rtk/query/model';

/**
 * Processes the result of a Redux Toolkit Query (RTK Query) promise and wraps the result in an `AsyncResult`.
 * This function expects the promise to resolve with either a `data` object or an `error` object.
 * If a data object is present, it wraps the data in a success result (`Result.Ok`).
 * If an error object is present, it wraps the error in a failure result (`Result.Err`).
 * If neither is present or the structure deviates, an error is thrown.
 * @example ```ts
 * const [update] = coreApi.useUpdateMutation();
 * const onSubmit = async (formValues) => {
 *  const res = await resultFromRTK(update(formValues))
 *  // Typed error here
 *  res.tapError(err => passErrorToForm(err, form))
 * }
 * ```
 */
export const resultFromRTK = <D>(
  p: Promise<{ data: D; error?: undefined } | { data?: undefined; error: SerializedError | ApiServerError }>,
): AsyncResult<D, SerializedError | ApiServerError> =>
  p.then((rtkResult) => {
    if (rtkResult.error) {
      return Result.Err(rtkResult.error);
    } else if ('data' in rtkResult) {
      return Result.Ok(rtkResult.data);
    } else {
      // We expect either data or error object from RTK
      throw new Error('Unexpected data in RTK query promise');
    }
  });
